import { useEffect, useMemo, useRef, useState } from 'react';
import { create } from 'zustand';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { type SearchDoc, entityLabel, fmtShort, searchAll, searchableText, statusName } from '@quadro/domain';
import { Icon } from '../../components/Icon';
import type { IconName } from '../../components/icons';
import * as api from '../../data/api';
import { useEvents, useNotes, useRoutines, useTasks, useTemplates } from '../../data/queries';
import { hud } from '../../components/Hud';
import { openSheet } from '../../state/sheets';
import { useUI } from '../../state/ui';
import { useWorkspace } from '../../state/workspace';
import { useMapState } from '../orgmap/mapState';
import { useEntities } from '../shared/useMap';

/** Busca geral aberta ou não (Ctrl+K em qualquer tela, ou "Buscar" na barra lateral). */
export const usePalette = create<{ open: boolean }>(() => ({ open: false }));
export const openPalette = () => usePalette.setState({ open: true });

type Kind = 'action' | 'template' | 'task' | 'routine' | 'process' | 'entity' | 'note' | 'event' | 'person';
interface Item extends SearchDoc { kind: Kind; icon: IconName; meta?: string; done?: boolean; run: () => void }

const GROUP: Record<Kind, string> = {
  action: 'Ações', template: 'Novas tarefas de modelos', task: 'Tarefas', routine: 'Rotinas', process: 'Processos', entity: 'Mapa societário',
  note: 'Minhas notas', event: 'Agenda', person: 'Pessoas',
};
const ORDER: Kind[] = ['action', 'template', 'task', 'routine', 'process', 'entity', 'person', 'event', 'note'];

/**
 * Busca geral (Ctrl+K): tarefas (inclusive concluídas), rotinas, processos, fundos e sociedades do mapa,
 * pessoas, eventos da agenda e as próprias notas, além de atalhos para as seções. Tudo no navegador,
 * a partir do que a tela já carregou; o mapa e os processos carregam quando a busca abre.
 */
export function CommandPalette({ onClose }: { onClose: () => void }) {
  const ws = useWorkspace();
  const nav = useNavigate();
  const ui = useUI();
  const tasks = useTasks().data ?? [];
  const routines = useRoutines().data ?? [];
  const notes = useNotes().data ?? [];
  const templates = useTemplates().data ?? [];
  const events = useEvents().data ?? [];
  const processes = useQuery({ queryKey: ['processes'], queryFn: api.fetchProcesses }).data ?? [];
  const entities = useEntities();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => { input.current?.focus(); }, []);

  const go = (fn: () => void) => () => { onClose(); fn(); };
  const person = (id: string | null) => (id ? ws.people.find(p => p.id === id)?.name : undefined);

  const actions: Item[] = useMemo(() => {
    const a: (Omit<Item, 'kind' | 'id'> & { when?: boolean })[] = [
      { title: 'Nova tarefa', icon: 'plus', text: 'criar adicionar capturar', when: ws.canWrite, run: () => { nav('/'); setTimeout(() => document.getElementById('capIn')?.focus(), 60); } },
      { title: 'Minhas tarefas', icon: 'me', run: () => { ui.setScope({ project: 'mine', person: 'all' }); nav('/'); } },
      { title: 'Quadro', icon: 'board', text: 'kanban todos os projetos', run: () => { ui.setScope({ project: 'all' }); nav('/'); } },
      { title: 'Rotinas', icon: 'recL', text: 'recorrências', run: () => nav('/rotinas') },
      { title: 'Mapa societário', icon: 'mapa', text: 'fundos sociedades', run: () => nav('/mapa') },
      { title: 'Processos', icon: 'flow', text: 'fluxo de dados desenhador', run: () => nav('/processos') },
      { title: 'Agenda', icon: 'cal', text: 'calendário eventos', run: () => nav('/agenda') },
      { title: 'Notas', icon: 'note', run: () => nav('/notas') },
      { title: 'Avisos', icon: 'bell', text: 'notificações menções', run: () => openSheet({ kind: 'notifications' }) },
      { title: 'Pessoas', icon: 'people', text: 'equipe acesso', run: () => openSheet({ kind: 'people' }) },
      { title: 'Ajustes da área', icon: 'gear', text: 'projetos temas taxonomia', when: ws.canWrite, run: () => openSheet({ kind: 'settings' }) },
      { title: 'Modelos de tarefa', icon: 'compose', text: 'checklist template', run: () => openSheet({ kind: 'templates' }) },
      { title: 'Nova rotina', icon: 'recL', text: 'criar recorrência', when: ws.canWrite, run: () => { nav('/rotinas'); openSheet({ kind: 'routine', id: null }); } },
    ];
    return a.filter(x => x.when !== false).map((x, i) => ({ ...x, id: `a${i}`, kind: 'action' as const }));
  }, [ws.canWrite, nav, ui]);

  const docs: Item[] = useMemo(() => [
    ...actions,
    ...(ws.canWrite ? templates : []).map(m => ({
      id: m.id, kind: 'template' as const, icon: 'plus' as const, title: `Nova tarefa: ${m.name}`, text: `${m.title} modelo checklist`,
      meta: m.subtasks.length ? `${m.subtasks.length} subtarefas` : undefined,
      run: () => { api.applyTemplate(m.id).then(({ id }) => { hud(`Tarefa criada do modelo “${m.name}”`); openSheet({ kind: 'task', id }); }, e => hud((e as Error).message)); },
    })),
    ...tasks.map(t => ({
      id: t.id, kind: 'task' as const, icon: 'board' as const, title: t.title, done: t.status === 'done',
      text: [t.description, t.waiting?.who, person(t.assigneeId), ...t.entityIds.map(entities.name), ...t.subtasks.map(s => s.text), ...t.links.map(l => l.title)].filter(Boolean).join(' '),
      meta: [statusName(t.status), person(t.assigneeId), t.due && `prazo ${fmtShort(t.due)}`].filter(Boolean).join(' · '),
      run: () => openSheet({ kind: 'task', id: t.id }),
    })),
    ...routines.map(r => ({
      id: r.id, kind: 'routine' as const, icon: 'recL' as const, title: r.title, text: `${r.description} ${person(r.assigneeId) ?? ''}`,
      meta: [r.active ? null : 'pausada', person(r.assigneeId)].filter(Boolean).join(' · '),
      run: () => { nav('/rotinas'); openSheet({ kind: 'routine', id: r.id }); },
    })),
    ...processes.map(p => ({
      id: p.id, kind: 'process' as const, icon: 'flow' as const, title: p.name, text: p.description,
      meta: `${p.nodeCount} caixas`, run: () => nav(`/processos/${p.id}`),
    })),
    ...entities.list.map(n => ({
      id: n.id, kind: 'entity' as const, icon: 'ent' as const, title: entityLabel(n), text: searchableText(n), meta: n.s,
      run: () => { useMapState.getState().set({ goTo: n.id, selected: n.id }); ui.setMap({ tab: 'mapa' }); nav('/mapa'); },
    })),
    ...ws.members.map(p => ({
      id: p.id, kind: 'person' as const, icon: 'me' as const, title: p.name, text: p.email,
      meta: `${tasks.filter(t => t.assigneeId === p.id && t.status !== 'done').length} tarefas abertas`,
      run: () => { ui.setScope({ project: 'all', person: p.id }); nav('/'); },
    })),
    ...events.map(e => ({
      id: e.id, kind: 'event' as const, icon: 'cal' as const, title: e.title, text: e.location, meta: `${fmtShort(e.date)} ${e.start}`,
      run: () => openSheet({ kind: 'event', id: e.id }),
    })),
    ...notes.map(n => ({
      id: n.id, kind: 'note' as const, icon: 'note' as const, title: n.title || 'Nova nota', text: n.body,
      run: () => { ui.set({ selectedNote: n.id, notesFilter: 'all', noteSearch: '' }); nav('/notas'); },
    })),
  ], [actions, templates, tasks, routines, processes, entities, ws.members, events, notes]);

  const results = useMemo(() => {
    if (!q.trim()) {
      // Sem texto: atalhos e as minhas tarefas abertas mais urgentes.
      const mine = docs.filter(d => d.kind === 'task' && !d.done && tasks.find(t => t.id === d.id)?.assigneeId === ws.me.id)
        .sort((a, b) => (tasks.find(t => t.id === a.id)?.due ?? '9').localeCompare(tasks.find(t => t.id === b.id)?.due ?? '9')).slice(0, 5);
      return [...actions.slice(0, 8), ...mine];
    }
    // Concluídas aparecem, mas depois das abertas com a mesma relevância.
    const found = searchAll(docs, q, 6);
    return ORDER.flatMap(k => found.filter(d => d.kind === k).sort((a, b) => Number(!!a.done) - Number(!!b.done)));
  }, [q, docs, actions, tasks, ws.me.id]);

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => { listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [active]);

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = results[active]; if (r) go(r.run)(); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  }

  let last: Kind | null = null;
  return (
    <div className="cmdk-layer" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Busca geral">
        <div className="cmdk-in">
          <Icon name="search" />
          <input ref={input} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} role="combobox" aria-expanded="true"
            aria-controls="cmdkList" aria-activedescendant={results[active] ? `cmdk-${active}` : undefined}
            placeholder="Buscar tarefas, rotinas, processos, fundos, pessoas…" aria-label="Buscar em tudo" spellCheck={false} />
          <kbd>Esc</kbd>
        </div>
        <div className="cmdk-list" id="cmdkList" role="listbox" ref={listRef}>
          {!results.length && <p className="cmdk-empty">Nada encontrado para “{q}”.</p>}
          {results.map((r, i) => {
            const head = r.kind !== last ? <div className="cmdk-group" role="presentation">{!q.trim() && r.kind === 'task' ? 'Minhas tarefas' : GROUP[r.kind]}</div> : null;
            last = r.kind;
            return (
              <div key={`${r.kind}-${r.id}`} role="presentation">
                {head}
                <div id={`cmdk-${i}`} data-kind={r.kind} role="option" aria-selected={i === active} className={`cmdk-item ${r.done ? 'done' : ''}`}
                  onMouseMove={() => { if (i !== active) setActive(i); }} onClick={go(r.run)}>
                  <Icon name={r.icon} />
                  <span className="cmdk-t">{r.title}</span>
                  {r.meta && <span className="cmdk-m">{r.meta}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="cmdk-foot"><span><kbd>↑</kbd><kbd>↓</kbd> navegar</span><span><kbd>Enter</kbd> abrir</span><span><kbd>Ctrl</kbd><kbd>K</kbd> busca geral</span></div>
      </div>
    </div>
  );
}
